import { useAtomValue } from 'jotai'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import { IconButton } from '~/components/elements/IconButton'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { type StyledButtonProps } from '~/components/elements/StyledButton'
import { StyledInput } from '~/components/elements/StyledInput'
import { VStack } from '~/components/elements/VStack'
import { useFormField } from '~/hooks/useFormField'
import { ensureExists } from '~/utils'

import { dialogInfoAtom } from '../../atoms'
import { InputContainer } from '../InputContainer'

import { AttachedOptionSelector } from './AttachedOptionSelector'
import { type InputValues } from './utils'

export type OptionInputProps = {
  index: number
  onDelete: (index: number) => void
}

export const OptionInput = ({ index, onDelete }: OptionInputProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const nameField = useFormField(control, `options.${index}.text`)

  const handleDeleteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onDelete(index)
  }, [index, onDelete])

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )

  return (
    <InputContainer>
      <HStack spacing={0}>
        <IconButton
          buttonStyle="light"
          fillMode="flat"
          icon="delete"
          onClick={handleDeleteButtonClick}
        />

        <LabelingContainer label={t('option_label')}>
          <StyledInput {...nameField} />
        </LabelingContainer>
      </HStack>

      <VStack flex={1} spacing={4}>
        {surveyGroup.surveys.map((survey) => (
          <AttachedOptionSelector
            key={survey.id}
            index={index}
            survey={survey}
          />
        ))}
      </VStack>
    </InputContainer>
  )
}
